import * as _ from 'lodash-es';
import React, { useMemo } from 'react';
import UserProfileAvatarEditor from './UserProfileAvatarEditor';
import useUpdateUserProfile from './useUpdateUserProfile';
import Card from '../../components/Card';
import Form, { Field, Submit } from '../../components/Form';
import RichTextDescriptionControl from '../../editor/RichTextDescriptionControl';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import SettingsSection from '../../settings/SettingsSection';
import SettingsSectionFooter from '../../settings/SettingsSectionFooter';
import SettingsSubmitConfirmation, {
  useSubmitWithSaved,
} from '../../settings/SettingsSubmitConfirmation';

const USER_FRAGMENT = gql(`
  fragment UserProfileSettingsForm_user on User {
    id
    name
    title
    description
    ...UserProfileAvatarEditor_user
  }
`);

interface UserProfileSettingsFormProps {
  onComplete?: () => void;
  user: DocumentType<typeof USER_FRAGMENT>;
}

type FormValues = {
  name?: string | null;
  title?: string | null;
  description?: string | null;
};

const UserProfileSettingsForm = ({
  user,
  onComplete,
}: UserProfileSettingsFormProps) => {
  const renderKey = useMemo(() => _.uniqueId(user.id), [user]);
  const updateUserProfile = useUpdateUserProfile({
    user,
  });

  const { saved: isSaved, handleSubmit } = useSubmitWithSaved(
    ({ name, title, description }: FormValues) =>
      updateUserProfile({
        name,
        title,
        description: JSON.stringify(description),
      }).then(() => onComplete?.()),
  );

  return (
    <>
      <SettingsSection header="Picture">
        <Card>
          <UserProfileAvatarEditor user={user} />
        </Card>
      </SettingsSection>
      <SettingsSection header="Details">
        <Form<FormValues>
          key={renderKey}
          initialValues={{
            name: user.name,
            title: user.title,
            description: user.description,
          }}
          onSubmit={handleSubmit}
        >
          <Field name="name" label="Name" required />
          <Field name="title" label="Title" />
          <Field
            label="Description"
            name="description"
            placeholder="Tell your team about yourself..."
            // @ts-expect-error
            component={RichTextDescriptionControl}
          />
          <SettingsSectionFooter>
            {isSaved && <SettingsSubmitConfirmation />}
            <Submit>Save</Submit>
          </SettingsSectionFooter>
        </Form>
      </SettingsSection>
    </>
  );
};

export default UserProfileSettingsForm;
